import {Injectable} from '@angular/core';
import {Router} from "@angular/router";
import {BehaviorSubject, distinctUntilChanged} from "rxjs";
import {StoreService} from "../store.service";
import {UserService} from "../user.service";
import {map, take} from "rxjs/operators";
import { ProfileDataPopupFunzionalitaEstese, UserProfile } from "../../models/personal-area/user/user-profile";
import {PosizioniJobService} from "../posizioni-job.service";
import jwt_decode, {JwtPayload} from "jwt-decode";
import {PersonalUserData} from "../../models/personal-area/user/personal-user-data";
import {OidcSecurityService} from "angular-auth-oidc-client";

export const USER_TOKEN = 'efil-jwt-user-token';
export const LAST_MODIFIED = 'lastModified';
const TOKEN = 'token';
const AUTH = 'auth';
const AUTHORITY = 'authority';
const POSIZIONI_JOB_STARTED = 'posizioni-job-started';

export type DecodedJWT = JwtPayload & PersonalUserData

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private decodedJWT = new BehaviorSubject<DecodedJWT | undefined>(undefined);
  public decodedJWT$ = this.decodedJWT.asObservable().pipe(distinctUntilChanged());
  public isLogged$ = this.decodedJWT.pipe(map(d => !!d), distinctUntilChanged());
  public firstAccess: boolean = false;

  constructor(
    private router: Router,
    private storeService: StoreService,
    private userService: UserService,
    private oidcSecurityService: OidcSecurityService,
    private posizioniJobService: PosizioniJobService
  ) {
    this.getAuthorityClientId();
  }

  getAuthorityClientId() {
    const authority = localStorage.getItem('authority') ?? '';
    if(authority && authority != null && authority != '' && authority != 'undefined') {
      const authorityObj = JSON.parse(authority);
      if(authorityObj && authorityObj.configurazioneAutenticazione && authorityObj.configurazioneAutenticazione.clientId) {
        const clientId = authorityObj.configurazioneAutenticazione.clientId;
        if(clientId != '') {
          try {
            this.storeService.authority = authorityObj;
            this.storeService.authoritySubject.next(authorityObj)

            // TODO: DEBUG in localhost - commentare prima della commit
            // this.login(window.sessionStorage.getItem('efil-jwt-user-token') || '');

            this.oidcSecurityService.checkAuth().pipe(take(1)).subscribe((authResult: any) => {
              const {isAuthenticated, accessToken} = authResult || {};
              if (isAuthenticated) {
                this.login(accessToken)
              } else {
                this.logout()
              }
            });
          } catch (error) {
            console.error('### getAuthorityClientId', error);
          }
        } else {
          // ci può essere un ente che non ha il clientId; in questo caso elimino solo gli eventuali dati utente senza fare il redirect alla HP
          this.logout();
        }
      } else {
        // ci può essere un ente che non ha il clientId; in questo caso elimino solo gli eventuali dati utente senza fare il redirect alla HP
        this.logout();
      }
    }
  }

  readonly isLogged = (): boolean => !!window.sessionStorage.getItem(USER_TOKEN);

  logout() {
    const userToken = window.sessionStorage.getItem(USER_TOKEN)
    if (userToken != null) {
      const jwtUserTokenDecoded = jwt_decode<DecodedJWT>(userToken);
      this.posizioniJobService
        .endPosizioniJob(jwtUserTokenDecoded.fiscalNumber, jwtUserTokenDecoded.codiceIpa)
        .subscribe()
      // remove guard flag so next login can start the job again
      try {
        const key = `${POSIZIONI_JOB_STARTED}-${jwtUserTokenDecoded.codiceIpa}-${jwtUserTokenDecoded.fiscalNumber}`;
        window.sessionStorage.removeItem(key);
      } catch (e) {
        console.debug('Could not remove posizioni job flag', e);
      }
    }
    this.userService.userProfile = null;

    // Rimuovi le informazioni di sessione dallo storage del browser
    window.sessionStorage.removeItem(TOKEN);
    window.sessionStorage.removeItem(USER_TOKEN);
    window.sessionStorage.removeItem(AUTH);
    this.decodedJWT.next(undefined)
    localStorage.removeItem(LAST_MODIFIED);
    localStorage.removeItem(AUTHORITY);
    // Reset AUTHORITY
    this.storeService.authority = undefined;
    this.storeService.codIpa = '';
  }

  logoutAndGoHome() {
    this.logout();
    this.goHome();
  }

  logoutAndGoServiceWithoutRegistration() {
    this.logout();
    this.goServiceWithoutRegistration();
  }

  goHome() {
    this.router.navigate(['/']); // Effettua il reindirizzamento alla Homepage
  }

  goServiceWithoutRegistration() {
    this.router.navigate(['/', this.storeService.codIpa, 'services-without-registration']);
  }

  public login(jwtUserToken: string, redirect: boolean = true) {
    window.sessionStorage.removeItem(USER_TOKEN);
    window.sessionStorage.setItem(USER_TOKEN, jwtUserToken);

    const jwtUserTokenDecoded = jwt_decode<DecodedJWT>(jwtUserToken);
    this.decodedJWT.next(jwtUserTokenDecoded)
    this.storeService.codIpa = jwtUserTokenDecoded.codiceIpa.toUpperCase();
    this.storeService.getAuthorityInfo()
    this.setUserAccess(jwtUserTokenDecoded, this.storeService.codIpa)

    const dashboardRoute = '/personal/dashboard';
    const currentUrl = window.location.href;
    const urlWithoutOrigin = currentUrl.replace(window.location.origin, '');
    if (redirect) {
      if(!window.location.href.includes('/payment-return/cart/cart-result')) {
        if (!!this.storeService.codIpa && this.storeService.codIpa !== '') {
          // [x]: recuperare la QS per navigare nella pagina opportuna; l'accesso da URL a qualsiasi pagina riporta sempre alla DASHBOARD
          if(urlWithoutOrigin == '/auth-redirect' || urlWithoutOrigin.includes('auth-redirect')) {
            this.router.navigate(['/' + this.storeService.codIpa + '/' + dashboardRoute]);
          } else {
            this.router.navigate([urlWithoutOrigin]);
          }
        } else {
          // Non dovrebbe mai verificarsi
          this.router.navigate(['/']);
        }
      }
    } else {
      if (!!this.storeService.codIpa && this.storeService.codIpa !== '') {
        this.router.navigate(['/' + this.storeService.codIpa + '/' + dashboardRoute]);
      } else {
        this.router.navigate(['/']);
      }
    }
  }

  navigateToLoginPage() {
    if (!!this.storeService.codIpa && this.storeService.codIpa !== '') {
      // noinspection JSIgnoredPromiseFromCall
      this.router.navigate(['/' + this.storeService.codIpa + '/auth']);
    } else {
      this.router.navigate(['/']);
    }
  }

  private setUserAccess({fiscalNumber, email, given_name, family_name, sub}: DecodedJWT, codiceIpa: string) {
    this.firstAccess = false;

    if (codiceIpa !== '' && !!sub) {
      this.userService.getUserProfile(fiscalNumber, codiceIpa)
        .subscribe({
          next: (res: UserProfile) => {
            this.userService.userProfile = res;
            this.userService.setUserAccess({
              id: sub,
              creationDate: undefined,
              lastModified: undefined,
              name: given_name,
              surname: family_name,
              email: email,
              codiceIpaEnte: codiceIpa,
              codiceFiscale: fiscalNumber,
              consensoAreaEstesa: res.consensoAreaEstesa,                 // Deliverable-006: recupero "consensoAreaEstesa" dal profilo dell'utente
              isPagamentoVeloceAbilitato: res.isPagamentoVeloceAbilitato  // Deliverable-006: recupero "isPagamentoVeloceAbilitato" dal profilo dell'utente
            }).subscribe({
              next: (userProfileData: UserProfile) => {
                this.userService.userProfile = userProfileData;
                this.startPosizioniJobOnce(userProfileData.codiceFiscale, codiceIpa, userProfileData.id, email);
              },
              error: (err: any) => {
                console.error(err)
              }
            })
          },
          error: (error: any) => {
            this.firstAccess = true;
            this.userService.setUserAccess({
              id: sub,
              creationDate: undefined,
              lastModified: undefined,
              name: given_name,
              surname: family_name,
              email: email,
              codiceIpaEnte: codiceIpa,
              codiceFiscale: fiscalNumber,
              // consensoAreaEstesa: this.storeService.authority?.autorizzazioneModelloTre ?? false,        // Recuperare dall'ente
              // isPagamentoVeloceAbilitato: this.storeService.authority?.autorizzazioneModelloTre ?? false // Recuperare dall'ente
              consensoAreaEstesa: false,        // CR - 05/2024: Al 1° accesso deve essere sempre FALSE
              isPagamentoVeloceAbilitato: false // CR - 05/2024: Al 1° accesso deve essere sempre FALSE
            }).subscribe({
              next: (userProfileData: UserProfile) => {
                this.userService.userProfile = userProfileData;
                this.startPosizioniJobOnce(userProfileData.codiceFiscale, codiceIpa, userProfileData.id, email);
              },
              error: (err: any) => {
                console.error(err)
              }
            })
          }
        });
    }
  }

  private startPosizioniJobOnce(codiceFiscale: string, codiceIpaEnte: string, uidUtente: string, email: string) {
    try {
      const key = `${POSIZIONI_JOB_STARTED}-${codiceIpaEnte}-${codiceFiscale}`;
      // se esiste il flag, evito di richiamare di nuovo
      if (window.sessionStorage.getItem(key)) {
        return;
      }
      // segno che ho già avviato la job per questa sessione/utente
      window.sessionStorage.setItem(key, '1');

      this.posizioniJobService
        .startPosizioniJob(codiceFiscale, codiceIpaEnte, uidUtente, email)
        .subscribe({
          next: () => {
            // nulla da fare: job avviato
          },
          error: (err: any) => {
            // se fallisce tolgo il flag così un successivo tentativo può ritentare
            window.sessionStorage.removeItem(key);
            console.error('startPosizioniJob error', err);
          }
        });
    } catch (e) {
      console.error('startPosizioniJobOnce error', e);
    }
  }

  getDecodedJWT(): DecodedJWT | undefined {
    const value = this.decodedJWT.getValue();
    return !!value ? {...value} : undefined
  }
}
